import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { useToast } from 'components/ui/toast';
import { tutorialsApi, type TutorialVideo } from 'api/tutorialsApi';

interface DeleteTutorialDialogProps {
  video: TutorialVideo | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteTutorialDialog = ({ video, onClose, onDeleted }: DeleteTutorialDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const close = () => {
    setFailed(false);
    onClose();
  };

  const confirm = async () => {
    if (!video || pending) return;
    setPending(true);
    setFailed(false);
    try {
      await tutorialsApi.remove(video.id);
      onDeleted();
      toast.success(t('Tutorial deleted'));
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <ConfirmDeleteDialog
      open={video !== null}
      title={t('Delete this tutorial?')}
      description={t('“{{title}}” will be permanently deleted.', { title: video?.title ?? '' })}
      loading={pending}
      error={failed ? t("Couldn't delete the video") : null}
      onConfirm={confirm}
      onClose={close}
    />
  );
};

export default DeleteTutorialDialog;
